import { useMemo, useRef, useState } from 'react';
import {
  App,
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Switch,
  Tooltip,
} from 'antd';
import {
  BoldOutlined,
  ClearOutlined,
  CodeOutlined,
  DeleteColumnOutlined,
  DeleteOutlined,
  DeleteRowOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  InsertRowBelowOutlined,
  ItalicOutlined,
  LinkOutlined,
  MinusOutlined,
  OrderedListOutlined,
  PictureOutlined,
  RedoOutlined,
  StrikethroughOutlined,
  TableOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
  VideoCameraOutlined,
  CopyOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import Editor, { type OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { marked, type Tokens } from 'marked';
import DOMPurify from 'dompurify';
import { useThemeStore } from '@presentation/stores/themeStore';
import {
  addTableColumn,
  addTableRow,
  applyLinePrefix,
  buildImageMarkup,
  buildLinkMarkup,
  buildTableMarkup,
  buildVideoMarkup,
  clearFormatting,
  deleteTableColumn,
  deleteTableRow,
  findTableBlock,
  prefixLines,
  resolveVideoEmbed,
  wrapSelection,
  type TableBlock,
} from '@presentation/services/markdownToolbarActions';

interface MarkdownEditorProps {
  /** Optional so this can sit inside an antd `Form.Item`, which injects `value`/`onChange` at runtime (invisible to the type checker) rather than them being passed explicitly in JSX. */
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
}

const PROSE_CLASSES = [
  '[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:first:mt-0',
  '[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:first:mt-0',
  '[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-base [&_h3]:font-semibold',
  '[&_p]:mb-2 [&_p]:leading-relaxed',
  '[&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:mb-0.5',
  '[&_a]:text-blue-500 [&_a]:underline',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-black/20 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-gray-500 dark:[&_blockquote]:border-white/20',
  '[&_code]:rounded [&_code]:bg-black/[0.06] [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[13px] dark:[&_code]:bg-white/10',
  '[&_pre]:mb-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-black/[0.06] [&_pre]:p-3 dark:[&_pre]:bg-white/10',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_table]:mb-2 [&_table]:border-collapse',
  '[&_th]:border [&_th]:border-black/10 [&_th]:px-2 [&_th]:py-1 dark:[&_th]:border-white/10',
  '[&_td]:border [&_td]:border-black/10 [&_td]:px-2 [&_td]:py-1 dark:[&_td]:border-white/10',
  '[&_hr]:my-3 [&_hr]:border-black/10 dark:[&_hr]:border-white/10',
  '[&_img]:max-w-full [&_img]:rounded',
  '[&_iframe]:max-w-full [&_iframe]:rounded',
  '[&_video]:max-w-full [&_video]:rounded',
].join(' ');

const CODE_LANGUAGES = [
  'json',
  'javascript',
  'typescript',
  'php',
  'python',
  'html',
  'css',
  'sql',
  'bash',
  'xml',
  'yaml',
  'plaintext',
];

marked.setOptions({ breaks: true, gfm: true });

const escapeAttr = (value: string): string => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

/**
 * A custom renderer so fenced code blocks come with a real "Copy" button in
 * Preview — marked's default `<pre><code>` has nowhere to hang interactive
 * behavior, so the raw code is embedded in a `data-code` attribute and a
 * single delegated click handler on the preview container reads it back out.
 */
const renderer = new marked.Renderer();
renderer.code = ({ text, lang }: Tokens.Code): string => {
  const language = (lang ?? '').split(/\s+/)[0] || 'plaintext';
  return `<div class="md-code-block" data-lang="${escapeAttr(language)}"><button type="button" class="md-copy-code-btn" data-code="${escapeAttr(text)}">Copy</button><pre><code class="language-${escapeAttr(language)}">${escapeAttr(text)}</code></pre></div>`;
};

type Mode = 'write' | 'preview' | 'split';

interface LinkFormValues {
  text: string;
  url: string;
  title?: string;
  openInNewTab?: boolean;
}

interface ImageFormValues {
  url: string;
  alt: string;
  title?: string;
  widthPx?: number;
}

interface VideoFormValues {
  url: string;
}

interface TableFormValues {
  rows: number;
  cols: number;
}

export const MarkdownEditor = ({
  value = '',
  onChange = () => {},
  placeholder,
  minHeight = 200,
}: MarkdownEditorProps) => {
  const { message } = App.useApp();
  const [mode, setMode] = useState<Mode>('write');
  const [fullscreen, setFullscreen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [codeLang, setCodeLang] = useState('json');
  const [currentTableBlock, setCurrentTableBlock] = useState<TableBlock | null>(null);
  const themeMode = useThemeStore((state) => state.mode);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const [linkForm] = Form.useForm<LinkFormValues>();
  const [imageForm] = Form.useForm<ImageFormValues>();
  const [videoForm] = Form.useForm<VideoFormValues>();
  const [tableForm] = Form.useForm<TableFormValues>();

  const html = useMemo(() => {
    if (!value.trim()) return '';
    const raw = marked.parse(value, { async: false, renderer });
    return DOMPurify.sanitize(raw, {
      ADD_TAGS: ['iframe', 'video', 'source'],
      ADD_ATTR: [
        'allow',
        'allowfullscreen',
        'frameborder',
        'controls',
        'width',
        'height',
        'sandbox',
        'target',
        'data-code',
        'data-lang',
      ],
    });
  }, [value]);

  const handleMount: OnMount = (editorInstance, monacoNs) => {
    editorRef.current = editorInstance;

    editorInstance.onDidChangeCursorPosition((event) => {
      const model = editorInstance.getModel();
      setCurrentTableBlock(model ? findTableBlock(model, event.position.lineNumber) : null);
    });

    editorInstance.addCommand(monacoNs.KeyMod.CtrlCmd | monacoNs.KeyCode.KeyB, () =>
      applyWrap('**', 'bold text'),
    );
    editorInstance.addCommand(monacoNs.KeyMod.CtrlCmd | monacoNs.KeyCode.KeyI, () =>
      applyWrap('_', 'italic text'),
    );
  };

  const replaceSelection = (text: string, selectFrom?: number, selectTo?: number) => {
    const ed = editorRef.current;
    const model = ed?.getModel();
    const selection = ed?.getSelection();
    if (!ed || !model || !selection) return;

    ed.executeEdits('markdown-toolbar', [{ range: selection, text, forceMoveMarkers: true }]);

    if (selectFrom !== undefined && selectTo !== undefined) {
      const startOffset = model.getOffsetAt({
        lineNumber: selection.startLineNumber,
        column: selection.startColumn,
      });
      const from = model.getPositionAt(startOffset + selectFrom);
      const to = model.getPositionAt(startOffset + selectTo);
      ed.setSelection({
        startLineNumber: from.lineNumber,
        startColumn: from.column,
        endLineNumber: to.lineNumber,
        endColumn: to.column,
      });
    }
    ed.focus();
  };

  const getSelectedText = (): string => {
    const ed = editorRef.current;
    const selection = ed?.getSelection();
    const model = ed?.getModel();
    if (!ed || !model || !selection) return '';
    return model.getValueInRange(selection);
  };

  const applyWrap = (marker: string, placeholder: string) => {
    const { text, selectStart, selectEnd } = wrapSelection(getSelectedText(), marker, placeholder);
    replaceSelection(text, selectStart, selectEnd);
  };

  const applyLinePrefixToSelection = (makePrefix: (index: number) => string) => {
    const selected = getSelectedText();
    replaceSelection(prefixLines(selected, makePrefix));
  };

  const applyHeading = (prefix: string) => {
    const ed = editorRef.current;
    const model = ed?.getModel();
    const position = ed?.getPosition();
    if (!ed || !model || !position) return;

    const line = model.getLineContent(position.lineNumber);
    const newLine = applyLinePrefix(line, prefix);
    ed.executeEdits('markdown-toolbar', [
      {
        range: {
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: line.length + 1,
        },
        text: newLine,
        forceMoveMarkers: true,
      },
    ]);
    ed.focus();
  };

  /**
   * @param cursorOffset  Offset (into `text`, after the leading newline is
   *   added) to place the cursor at once inserted — e.g. between a code
   *   block's fences, ready to type. Defaults to after the whole block,
   *   Monaco's normal post-edit cursor placement.
   */
  const insertBlock = (text: string, cursorOffset?: number) => {
    const ed = editorRef.current;
    const model = ed?.getModel();
    const selection = ed?.getSelection();
    if (!ed || !model || !selection) return;

    // Block-level markdown (tables, fenced code, raw HTML like the video
    // embed) only parses correctly when it sits in its own paragraph — glued
    // directly onto the end of a preceding line/block with a single '\n',
    // marked treats it as a continuation of that block and leaves it as
    // literal text instead of parsing it (confirmed empirically: a table
    // right after an iframe with no blank line renders as plain text).
    const needsBlankLineBefore =
      selection.startColumn > 1 ||
      (selection.startLineNumber > 1 &&
        model.getLineContent(selection.startLineNumber - 1).trim() !== '');
    const prefix = needsBlankLineBefore ? '\n\n' : '';
    ed.executeEdits('markdown-toolbar', [
      { range: selection, text: `${prefix}${text}\n\n`, forceMoveMarkers: true },
    ]);

    if (cursorOffset !== undefined) {
      const startOffset = model.getOffsetAt({
        lineNumber: selection.startLineNumber,
        column: selection.startColumn,
      });
      const position = model.getPositionAt(startOffset + prefix.length + cursorOffset);
      ed.setPosition(position);
    }
    ed.focus();
  };

  const handleClearFormatting = () => {
    const ed = editorRef.current;
    const selection = ed?.getSelection();
    const model = ed?.getModel();
    if (!ed || !selection || !model) return;
    const targetRange = selection.isEmpty()
      ? {
          startLineNumber: 1,
          startColumn: 1,
          endLineNumber: model.getLineCount(),
          endColumn: model.getLineMaxColumn(model.getLineCount()),
        }
      : selection;
    const text = model.getValueInRange(targetRange);
    ed.executeEdits('markdown-toolbar', [
      { range: targetRange, text: clearFormatting(text), forceMoveMarkers: true },
    ]);
    ed.focus();
  };

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(value);
      void message.success('Copied to clipboard');
    } catch {
      void message.error('Failed to copy — clipboard access was denied');
    }
  };

  const applyTableEdit = (nextBlockText: string) => {
    const ed = editorRef.current;
    if (!ed || !currentTableBlock) return;
    ed.executeEdits('markdown-table', [
      {
        range: {
          startLineNumber: currentTableBlock.startLine,
          startColumn: 1,
          endLineNumber: currentTableBlock.endLine,
          endColumn: 1 + currentTableBlock.lines[currentTableBlock.lines.length - 1].length,
        },
        text: nextBlockText,
        forceMoveMarkers: true,
      },
    ]);
    ed.focus();
  };

  const handleCopyTable = async () => {
    if (!currentTableBlock) return;
    try {
      await navigator.clipboard.writeText(currentTableBlock.lines.join('\n'));
      void message.success('Table copied');
    } catch {
      void message.error('Failed to copy — clipboard access was denied');
    }
  };

  const handleDeleteTable = () => {
    const ed = editorRef.current;
    if (!ed || !currentTableBlock) return;
    ed.executeEdits('markdown-table', [
      {
        range: {
          startLineNumber: currentTableBlock.startLine,
          startColumn: 1,
          endLineNumber: currentTableBlock.endLine + 1,
          endColumn: 1,
        },
        text: '',
        forceMoveMarkers: true,
      },
    ]);
    ed.focus();
  };

  const handlePreviewClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('.md-copy-code-btn');
    if (!target) return;
    const code = target.getAttribute('data-code') ?? '';
    try {
      await navigator.clipboard.writeText(code);
      void message.success('Code copied');
    } catch {
      void message.error('Failed to copy — clipboard access was denied');
    }
  };

  const editorNode = (
    <Editor
      height="100%"
      language="markdown"
      theme={themeMode === 'dark' ? 'vs-dark' : 'light'}
      value={value}
      onChange={(next) => onChange(next ?? '')}
      onMount={handleMount}
      options={{
        minimap: { enabled: false },
        fontSize: 13,
        wordWrap: 'on',
        lineNumbers: 'off',
        folding: false,
        scrollBeyondLastLine: false,
        padding: { top: 8, bottom: 8 },
      }}
    />
  );

  const previewNode = (
    <div
      onClick={(event) => void handlePreviewClick(event)}
      className={`overflow-y-auto rounded border border-black/10 p-3 text-sm dark:border-white/10 [&_.md-code-block]:relative [&_.md-copy-code-btn]:absolute [&_.md-copy-code-btn]:right-2 [&_.md-copy-code-btn]:top-2 [&_.md-copy-code-btn]:rounded [&_.md-copy-code-btn]:border [&_.md-copy-code-btn]:border-black/10 [&_.md-copy-code-btn]:bg-white/80 [&_.md-copy-code-btn]:px-1.5 [&_.md-copy-code-btn]:py-0.5 [&_.md-copy-code-btn]:text-[11px] [&_.md-copy-code-btn]:opacity-0 [&_.md-code-block:hover_.md-copy-code-btn]:opacity-100 dark:[&_.md-copy-code-btn]:border-white/10 dark:[&_.md-copy-code-btn]:bg-black/60 ${PROSE_CLASSES}`}
      style={{ height: '100%' }}
    >
      {html ? (
        <div dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <span className="text-gray-400">
          {placeholder ?? 'Nothing to preview yet — switch to Write.'}
        </span>
      )}
    </div>
  );

  const editorAreaHeight = fullscreen ? undefined : minHeight;

  return (
    <div
      className={
        fullscreen
          ? 'fixed inset-0 z-[1050] flex flex-col bg-white p-3 dark:bg-[#141414]'
          : 'flex flex-col gap-2'
      }
    >
      <div className="flex flex-wrap items-center gap-1 rounded border border-black/10 bg-black/[0.02] p-1.5 dark:border-white/10 dark:bg-white/[0.02]">
        <Tooltip title="Bold (Ctrl/Cmd+B)">
          <Button
            size="small"
            type="text"
            icon={<BoldOutlined />}
            aria-label="Bold"
            onClick={() => applyWrap('**', 'bold text')}
          />
        </Tooltip>
        <Tooltip title="Italic (Ctrl/Cmd+I)">
          <Button
            size="small"
            type="text"
            icon={<ItalicOutlined />}
            aria-label="Italic"
            onClick={() => applyWrap('_', 'italic text')}
          />
        </Tooltip>
        <Tooltip title="Strikethrough">
          <Button
            size="small"
            type="text"
            icon={<StrikethroughOutlined />}
            aria-label="Strikethrough"
            onClick={() => applyWrap('~~', 'strikethrough text')}
          />
        </Tooltip>
        <Tooltip title="Inline code">
          <Button
            size="small"
            type="text"
            icon={<CodeOutlined />}
            aria-label="Inline code"
            onClick={() => applyWrap('`', 'code')}
          />
        </Tooltip>

        <Divider vertical className="mx-0.5 h-5" />

        <Tooltip title="Heading 1">
          <Button
            size="small"
            type="text"
            aria-label="Heading 1"
            onClick={() => applyHeading('# ')}
          >
            H1
          </Button>
        </Tooltip>
        <Tooltip title="Heading 2">
          <Button
            size="small"
            type="text"
            aria-label="Heading 2"
            onClick={() => applyHeading('## ')}
          >
            H2
          </Button>
        </Tooltip>
        <Tooltip title="Heading 3">
          <Button
            size="small"
            type="text"
            aria-label="Heading 3"
            onClick={() => applyHeading('### ')}
          >
            H3
          </Button>
        </Tooltip>
        <Tooltip title="Paragraph (remove heading/list/quote)">
          <Button
            size="small"
            type="text"
            icon={<UnderlineOutlined rotate={90} />}
            aria-label="Paragraph"
            onClick={() => applyHeading('')}
          />
        </Tooltip>

        <Divider vertical className="mx-0.5 h-5" />

        <Tooltip title="Bulleted list">
          <Button
            size="small"
            type="text"
            icon={<UnorderedListOutlined />}
            aria-label="Bulleted list"
            onClick={() => applyLinePrefixToSelection(() => '- ')}
          />
        </Tooltip>
        <Tooltip title="Numbered list">
          <Button
            size="small"
            type="text"
            icon={<OrderedListOutlined />}
            aria-label="Numbered list"
            onClick={() => applyLinePrefixToSelection((i) => `${i + 1}. `)}
          />
        </Tooltip>
        <Tooltip title="Blockquote">
          <Button
            size="small"
            type="text"
            aria-label="Blockquote"
            onClick={() => applyLinePrefixToSelection(() => '> ')}
          >
            &ldquo;
          </Button>
        </Tooltip>
        <Tooltip title="Horizontal rule">
          <Button
            size="small"
            type="text"
            icon={<MinusOutlined />}
            aria-label="Horizontal rule"
            onClick={() => insertBlock('---')}
          />
        </Tooltip>

        <Divider vertical className="mx-0.5 h-5" />

        <Tooltip title="Insert link">
          <Button
            size="small"
            type="text"
            icon={<LinkOutlined />}
            aria-label="Insert link"
            onClick={() => {
              linkForm.setFieldsValue({
                text: getSelectedText(),
                url: '',
                title: '',
                openInNewTab: false,
              });
              setLinkModalOpen(true);
            }}
          />
        </Tooltip>
        <Tooltip title="Insert image">
          <Button
            size="small"
            type="text"
            icon={<PictureOutlined />}
            aria-label="Insert image"
            onClick={() => {
              imageForm.resetFields();
              setImageModalOpen(true);
            }}
          />
        </Tooltip>
        <Tooltip title="Insert video">
          <Button
            size="small"
            type="text"
            icon={<VideoCameraOutlined />}
            aria-label="Insert video"
            onClick={() => {
              videoForm.resetFields();
              setVideoModalOpen(true);
            }}
          />
        </Tooltip>
        <Tooltip title="Insert table">
          <Button
            size="small"
            type="text"
            icon={<TableOutlined />}
            aria-label="Insert table"
            onClick={() => {
              tableForm.setFieldsValue({ rows: 2, cols: 3 });
              setTableModalOpen(true);
            }}
          />
        </Tooltip>

        <Divider vertical className="mx-0.5 h-5" />

        <Select
          size="small"
          value={codeLang}
          onChange={setCodeLang}
          options={CODE_LANGUAGES.map((lang) => ({ label: lang, value: lang }))}
          className="w-28"
          aria-label="Code block language"
        />
        <Tooltip title="Insert code block">
          <Button
            size="small"
            type="text"
            aria-label="Insert code block"
            onClick={() => insertBlock(`\`\`\`${codeLang}\n\n\`\`\``, `\`\`\`${codeLang}\n`.length)}
          >
            {'{ }'}
          </Button>
        </Tooltip>

        <Divider vertical className="mx-0.5 h-5" />

        <Tooltip title="Undo">
          <Button
            size="small"
            type="text"
            icon={<UndoOutlined />}
            aria-label="Undo"
            onClick={() => editorRef.current?.trigger('toolbar', 'undo', undefined)}
          />
        </Tooltip>
        <Tooltip title="Redo">
          <Button
            size="small"
            type="text"
            icon={<RedoOutlined />}
            aria-label="Redo"
            onClick={() => editorRef.current?.trigger('toolbar', 'redo', undefined)}
          />
        </Tooltip>
        <Tooltip title="Clear formatting">
          <Button
            size="small"
            type="text"
            icon={<ClearOutlined />}
            aria-label="Clear formatting"
            onClick={handleClearFormatting}
          />
        </Tooltip>
        <Tooltip title="Copy all">
          <Button
            size="small"
            type="text"
            icon={<CopyOutlined />}
            aria-label="Copy all"
            onClick={() => void handleCopyAll()}
          />
        </Tooltip>

        <div className="ml-auto flex items-center gap-1">
          <Segmented
            size="small"
            value={mode}
            onChange={(next) => setMode(next as Mode)}
            options={[
              { label: 'Write', value: 'write' },
              { label: 'Preview', value: 'preview' },
              { label: 'Split', value: 'split' },
            ]}
          />
          <Tooltip title={fullscreen ? 'Restore' : 'Fullscreen'}>
            <Button
              size="small"
              type="text"
              icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              aria-label={
                fullscreen ? 'Restore documentation editor' : 'Fullscreen documentation editor'
              }
              onClick={() => setFullscreen((v) => !v)}
            />
          </Tooltip>
        </div>
      </div>

      {currentTableBlock && mode !== 'preview' && (
        <div className="flex flex-wrap items-center gap-1 rounded border border-black/10 bg-black/[0.02] px-1.5 py-1 text-xs dark:border-white/10 dark:bg-white/[0.02]">
          <span className="mr-1 text-gray-500">Table:</span>
          <Tooltip title="Add row">
            <Button
              size="small"
              type="text"
              icon={<InsertRowBelowOutlined />}
              aria-label="Add table row"
              onClick={() => applyTableEdit(addTableRow(currentTableBlock))}
            />
          </Tooltip>
          <Tooltip title="Delete current row">
            <Button
              size="small"
              type="text"
              icon={<DeleteRowOutlined />}
              aria-label="Delete table row"
              onClick={() => {
                const ed = editorRef.current;
                const line = ed?.getPosition()?.lineNumber;
                if (line === undefined) return;
                applyTableEdit(
                  deleteTableRow(currentTableBlock, line - currentTableBlock.startLine),
                );
              }}
            />
          </Tooltip>
          <Tooltip title="Add column">
            <Button
              size="small"
              type="text"
              icon={<PlusOutlined />}
              aria-label="Add table column"
              onClick={() => applyTableEdit(addTableColumn(currentTableBlock))}
            />
          </Tooltip>
          <Tooltip title="Delete last column">
            <Button
              size="small"
              type="text"
              icon={<DeleteColumnOutlined />}
              aria-label="Delete table column"
              onClick={() => {
                const colCount = (currentTableBlock.lines[0].match(/\|/g)?.length ?? 2) - 1;
                applyTableEdit(deleteTableColumn(currentTableBlock, Math.max(colCount - 1, 0)));
              }}
            />
          </Tooltip>
          <Tooltip title="Copy table">
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              aria-label="Copy table"
              onClick={() => void handleCopyTable()}
            />
          </Tooltip>
          <Tooltip title="Delete table">
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              aria-label="Delete table"
              onClick={handleDeleteTable}
            />
          </Tooltip>
        </div>
      )}

      <div
        className={fullscreen ? 'min-h-0 flex-1' : ''}
        style={fullscreen ? undefined : { height: editorAreaHeight }}
      >
        {mode === 'split' ? (
          <div className="flex h-full gap-2">
            <div className="min-w-0 flex-1 overflow-hidden rounded border border-black/10 dark:border-white/10">
              {editorNode}
            </div>
            <div className="min-w-0 flex-1">{previewNode}</div>
          </div>
        ) : mode === 'preview' ? (
          previewNode
        ) : (
          <div className="h-full overflow-hidden rounded border border-black/10 dark:border-white/10">
            {editorNode}
          </div>
        )}
      </div>

      <Modal
        title="Insert link"
        open={linkModalOpen}
        onCancel={() => setLinkModalOpen(false)}
        onOk={() =>
          linkForm.validateFields().then((values) => {
            replaceSelection(
              buildLinkMarkup({
                text: values.text,
                url: values.url,
                title: values.title,
                openInNewTab: values.openInNewTab,
              }),
            );
            setLinkModalOpen(false);
          })
        }
      >
        <Form form={linkForm} name="markdown-link-form" layout="vertical">
          <Form.Item
            name="text"
            label="Text"
            rules={[{ required: true, message: 'Link text is required' }]}
          >
            <Input placeholder="Documentation" />
          </Form.Item>
          <Form.Item
            name="url"
            label="URL"
            rules={[{ required: true, type: 'url', message: 'A valid URL is required' }]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>
          <Form.Item name="title" label="Title (optional)">
            <Input placeholder="Shown on hover" />
          </Form.Item>
          <Form.Item name="openInNewTab" label="Open in new tab" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Insert image"
        open={imageModalOpen}
        onCancel={() => setImageModalOpen(false)}
        onOk={() =>
          imageForm.validateFields().then((values) => {
            replaceSelection(
              buildImageMarkup({
                url: values.url,
                alt: values.alt,
                title: values.title,
                widthPx: values.widthPx,
              }),
            );
            setImageModalOpen(false);
          })
        }
      >
        <Form form={imageForm} name="markdown-image-form" layout="vertical">
          <Form.Item
            name="url"
            label="Image URL"
            rules={[{ required: true, type: 'url', message: 'A valid URL is required' }]}
          >
            <Input placeholder="https://example.com/screenshot.png" />
          </Form.Item>
          <Form.Item
            name="alt"
            label="Alt text"
            rules={[{ required: true, message: 'Alt text is required' }]}
          >
            <Input placeholder="Describes the image" />
          </Form.Item>
          <Form.Item name="title" label="Title (optional)">
            <Input placeholder="Shown on hover" />
          </Form.Item>
          <Form.Item name="widthPx" label="Width in px (optional — enables resizing)">
            <InputNumber min={16} max={4000} className="w-full" placeholder="e.g. 480" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Insert video"
        open={videoModalOpen}
        onCancel={() => setVideoModalOpen(false)}
        onOk={() =>
          videoForm.validateFields().then((values) => {
            const resolved = resolveVideoEmbed(values.url);
            const markup = buildVideoMarkup(resolved);
            if (!markup) {
              void message.error(
                'Only YouTube, Vimeo, or direct .mp4/.webm/.ogg URLs are supported.',
              );
              return;
            }
            insertBlock(markup);
            setVideoModalOpen(false);
          })
        }
      >
        <Form form={videoForm} name="markdown-video-form" layout="vertical">
          <Form.Item
            name="url"
            label="Video URL"
            rules={[{ required: true, message: 'A URL is required' }]}
            extra="YouTube, Vimeo, or a direct .mp4/.webm/.ogg link."
          >
            <Input placeholder="https://www.youtube.com/watch?v=..." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Insert table"
        open={tableModalOpen}
        onCancel={() => setTableModalOpen(false)}
        onOk={() =>
          tableForm.validateFields().then((values) => {
            insertBlock(buildTableMarkup(values.rows, values.cols));
            setTableModalOpen(false);
          })
        }
      >
        <Form form={tableForm} name="markdown-table-form" layout="vertical">
          <Form.Item name="rows" label="Rows" rules={[{ required: true }]}>
            <InputNumber min={1} max={30} className="w-full" />
          </Form.Item>
          <Form.Item name="cols" label="Columns" rules={[{ required: true }]}>
            <InputNumber min={1} max={12} className="w-full" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
